/**
 * 카톤 단위는 주문 수량만 묶는다. 배송을 나누지 않는다.
 * 오발송·누락 재발송도 주문번호는 그대로이고, 송장만 추가된다.
 */
import type { MallOrderShipment } from '@/api/mallOrder'

export function invoiceKey(value: string | null | undefined): string {
  return String(value || '').replace(/\D/g, '')
}

export function isSameWaybill(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = invoiceKey(left)
  const b = invoiceKey(right)
  return Boolean(a && b && a === b)
}

export function isCartonSplitMemo(memo: string | null | undefined): boolean {
  return /카톤\s*외|개별발송|낱개\s*수량/.test(String(memo || ''))
}

export function extraShipmentsOf(item: {
  invoice?: string | null
  shipments?: MallOrderShipment[] | null
}): MallOrderShipment[] {
  const extras = (item.shipments || []).filter((row) => row.kind === 'ADDITIONAL')
  const initial = (item.shipments || []).find((row) => row.kind === 'INITIAL')
  const mainInvoice = initial?.invoice || ''
  return extras
    .filter((row) => !mainInvoice || !isSameWaybill(mainInvoice, row.invoice))
    .map((row) => (isCartonSplitMemo(row.memo) ? { ...row, memo: '오발송·누락 재발송' } : row))
}

export interface ShipmentLedgerItem {
  invoice?: string | null
  courier?: string | null
  qty?: number
  shippedAt?: string | null
  trackStatus?: string | null
  shipments?: MallOrderShipment[] | null
}

export interface ShipmentLedgerRow {
  key: string
  kind: 'INITIAL' | 'ADDITIONAL'
  label: string
  courier: string
  invoice: string
  qty: number
  memo: string
  at: string | null
  liveStatus: string
  /**
   * 이 송장이 배송완료로 판정됐는지. 완료면 UI에서 초록 배지로 강조한다.
   * 항목(item) 대표 송장은 기존처럼 item.trackStatus 로도 판정하지만,
   * 여러 송장 케이스에서는 shipment.deliveredAt 을 우선으로 본다.
   */
  delivered: boolean
}

/**
 * 「배송완료」로 판정되는 상태 문구. 택배사 이벤트 이름(예: 배송완료, 배달완료, DELIVERED)이
 * 다양하므로 몇 가지 대표 표기를 함께 매치한다.
 */
export function isDeliveredStatus(status: string | null | undefined): boolean {
  return /배송\s*완료|배달\s*완료|DELIVERED/i.test(String(status || ''))
}

export function isExistingWaybill(
  item: ShipmentLedgerItem,
  invoice: string | null | undefined,
): boolean {
  if (isSameWaybill(item.invoice, invoice)) return true
  return (item.shipments || []).some((row) => isSameWaybill(row.invoice, invoice))
}

/**
 * 어떤 shipment 행의 「지금 이 순간」 상태를 정한다.
 * 1) shipment.deliveredAt 있으면 「배송완료」 (개별 송장 배송 확정)
 * 2) shipment.trackStatus 있으면 그 값
 * 3) shipment.invoice 가 아이템 대표 invoice 와 같으면 item.trackStatus (레거시)
 */
function resolveShipmentLiveStatus(
  item: ShipmentLedgerItem,
  ship: { invoice?: string | null; trackStatus?: string | null; deliveredAt?: string | null },
): { text: string; delivered: boolean } {
  if (ship.deliveredAt) {
    return { text: '배송완료', delivered: true }
  }
  const raw = String(ship.trackStatus || '').trim()
  if (raw) {
    return { text: raw, delivered: isDeliveredStatus(raw) }
  }
  if (isSameWaybill(ship.invoice, item.invoice)) {
    const fallback = String(item.trackStatus || '').trim()
    return { text: fallback, delivered: isDeliveredStatus(fallback) }
  }
  return { text: '', delivered: false }
}

export function shipmentLedgerOf(item: ShipmentLedgerItem): ShipmentLedgerRow[] {
  const extras = extraShipmentsOf(item)
  const initial = (item.shipments || []).find((row) => row.kind === 'INITIAL')
  const rows: ShipmentLedgerRow[] = []

  if (initial && (initial.courier || initial.invoice)) {
    const status = resolveShipmentLiveStatus(item, initial)
    rows.push({
      key: `init-${initial.idx}`,
      kind: 'INITIAL',
      label: '본발송',
      courier: initial.courier,
      invoice: initial.invoice,
      qty: Math.max(1, Number(initial.qty || item.qty) || 1),
      memo: '',
      at: initial.createdAt || item.shippedAt || null,
      liveStatus: status.text,
      delivered: status.delivered,
    })
  } else if (!extras.length && (item.invoice || item.courier)) {
    const fallback = String(item.trackStatus || '').trim()
    rows.push({
      key: 'init-item',
      kind: 'INITIAL',
      label: '본발송',
      courier: String(item.courier || ''),
      invoice: String(item.invoice || ''),
      qty: Math.max(1, Number(item.qty) || 1),
      memo: '',
      at: item.shippedAt || null,
      liveStatus: fallback,
      delivered: isDeliveredStatus(fallback),
    })
  }

  extras.forEach((ship, index) => {
    const status = resolveShipmentLiveStatus(item, ship)
    rows.push({
      key: `extra-${ship.idx}`,
      kind: 'ADDITIONAL',
      label: extras.length > 1 ? `재발송 ${index + 1}` : '재발송',
      courier: ship.courier,
      invoice: ship.invoice,
      qty: Math.max(1, Number(ship.qty) || 1),
      memo: ship.memo,
      at: ship.createdAt,
      liveStatus: status.text,
      delivered: status.delivered,
    })
  })

  return rows
}
